import _ from "underscore";
import DriveView from "../../../../../DriveView";
import { generateChecklist } from "../../../../framework/checklist";
import { dispatcher } from "../../../../../utils/dispatcher";
import idSerializer from "geotab-uuid";
import Sync from "../../../../../sync/sync";
import { getServerDate } from "../../../../../utils/storage/date";
import { handleJSNative } from "../../../../../utils/error";
import PageError from "../../../../../utils/pageError";
import { getCurrentVehicle } from "../../../../../utils/device";
import { Dialog } from "../../../../framework/dialog";
import { dstCalculator } from "../../../../../utils/date";
import { empty, jsonClone } from "../../../../../utils/vanilla";
import constants from "../../../../../constants";
import dayjs from "../../../../../utils/dayjs";
import { MobileActions } from "../../../../../store/mobile/actions";
import { PageLevel } from "../../../../../store/mobile/types";
import malfunctionHelpHTML from "../../../../malfunction.help.tmpl";
import diagnosticHelpHTML from "../../../../diagnostic.help.tmpl";
import pageHTML from "./malfunction.tmpl";
import malfunctionLogTemplate from "./malfunctionLog.tmpl";
import { uuid } from "geotab-uuid";
import DutyStatusLog from "../../../../../model/DutyStatusLog";

const HOS_SYNC_EVENT = "sync:hos";
const MALFUNCTION_TITLE = "Malfunction notifications";
const ACTIVE_TAB = "hos";
const MALFUNCTION_KEY = "#malfunction";
const DIAGNOSTIC_KEY = "#diagnostic";
const MANUAL_PROP = "Manual";
const DATE_FORMAT = "lll";
const CLEARED_REGEXP = ["UserClear", "SystemClear", "SystemClearDriving", "SystemDiagnosticClear",
    "SystemDiagnosticClearDriving", "UserDiagnosticClear", "UserMalfunctionClear"];
const diagnosticRegExp = /Diagnostic/gi;

/* eslint-disable @typescript-eslint/naming-convention */
class MalfunctionDutyStatusLog extends DutyStatusLog {
    _clearedTime?: string;
    _header?: boolean;
    _clearButton?: boolean;
    _helpButton?: boolean;
    _status?: string;
    _malfunctionType?: string;
    className?: string;
    _date?: string;
}
/* eslint-enable @typescript-eslint/naming-convention */

export default class HosEldMalfunctionView extends DriveView<Backbone.Model> {
    private readonly template = _.template(pageHTML);
    private readonly sync: Sync;
    private readonly errors;
    private readonly malfunctionHelpTemplate = _.template(malfunctionHelpHTML);
    private readonly diagnosticHelpTemplate = _.template(diagnosticHelpHTML);
    private displayLogCollection: MalfunctionDutyStatusLog[];

    constructor(options) {
        super(options, {
            title: MALFUNCTION_TITLE,
            activeSection: ACTIVE_TAB,
            pageLevel: PageLevel.TWO
        });

        this.sync = this.app.sync;
        this.displayLogCollection = this.populateAndGroupLogs(this.getFilteredLogs());
        this.errors = [];

        this.listenTo(dispatcher, HOS_SYNC_EVENT, this.updateLogs.bind(this));
        let pageChangeUnsubscribe = this.app.observeStore(state => state.mobile.currentPage, () => {
            // Diagnostic is marked as viewed when navigating away from the page
            this.app.store.dispatch(MobileActions.setDiagnosticViewed(true));
            // Then unsubscribe from this listener
            pageChangeUnsubscribe();
        });
    }

    /* eslint-disable @typescript-eslint/unbound-method */
    events() {
        return {
            "click .malfunction-clear-all button": this.addClearingLog,
            "click .malfunction-help svg": this.displayHelpDialog
        } as Backbone.EventsHash;
    }
    /* eslint-enable @typescript-eslint/unbound-method */

    render() {
        void this.display(this.template({
            logsExist: this.displayLogCollection.length,
            errors: this.errors.map(error => error.text)
        })).then(this.generateCheckList.bind(this));

        return this;
    }

    private populateAndGroupLogs(collection: DutyStatusLog[]): MalfunctionDutyStatusLog[] {
        // Filter UnidentifiedDrivingDiagnostics so that the diagnostics indicator does not show up for logs
        // that are older than 14 days in Canada and 7 days in US
        let filterStartDateMs = this.app.hosUtils.getUnidentifiedDrivingRetentionStartDate(this.app.store.getState().data.ELDJurisdiction === "CANELD").getTime();
        let groupedCollection = collection.reduce((acc, log) => {
            let groupKey = `${ log.status }${ (log.malfunction?.match(diagnosticRegExp) ? DIAGNOSTIC_KEY : MALFUNCTION_KEY) }`;
            if (log.status === "UnidentifiedDrivingCompliance" && new Date(log.dateTime).getTime() < filterStartDateMs) {
                return acc;
            }
            if (acc[groupKey]) {
                acc[groupKey].push(log);
            } else {
                acc[groupKey] = [log];
            }

            return acc;
        }, <DutyStatusLog>{});
        let resultCollection: MalfunctionDutyStatusLog[] = [];

        Object.keys(groupedCollection).forEach(groupKey => {
            let group: DutyStatusLog[]  = groupedCollection[groupKey];

            group.sort((log1, log2) => log2.getDate().getTime() - log1.getDate().getTime());

            let malfunctionLogsByType: MalfunctionDutyStatusLog[] = [];

            let clearedTime: string | undefined = undefined;
            group.forEach((log) => {
                if (log.malfunction && CLEARED_REGEXP.indexOf(log.malfunction) > -1) {
                    clearedTime = log.dateTime;
                } else {
                    let malfunctionLog = new MalfunctionDutyStatusLog(log);
                    malfunctionLog._clearedTime = clearedTime;
                    malfunctionLogsByType.push(malfunctionLog);
                }
            });

            if (malfunctionLogsByType.length) {
                this.createHeaderLog(malfunctionLogsByType);
                if (!malfunctionLogsByType[0]._clearedTime) {
                    resultCollection = malfunctionLogsByType.concat(resultCollection);
                } else {
                    resultCollection = resultCollection.concat(malfunctionLogsByType);
                }
            }
        });

        let malfunctionState = resultCollection.length > 0 && resultCollection.some(log => log.malfunction === "Malfunction" && !log._clearedTime);
        let diagnosticState = resultCollection.length > 0 && resultCollection.some(log => log.malfunction === "Diagnostic" && !log._clearedTime);
        this.populateNonHeaderLogs(resultCollection);

        this.app.store.dispatch(MobileActions.setEldMalfunction(malfunctionState));
        this.app.store.dispatch(MobileActions.setEldDiagnostic(diagnosticState));

        return resultCollection;
    }

    private loadLogs() {
        return this.app.dutyStatusLogService.getHOSLogs(true, undefined, false, true, true).filter((log) => !log.isPositioningComplianceDiagnosticLog());
    }

    private filterMalfunctions(this: void, log: DutyStatusLog) {
        let currentVehicle = getCurrentVehicle();
        return currentVehicle && log.isMalfunctionLog() && log.getDeviceId() === currentVehicle.id;
    }

    private generateCheckList() {
        let listContainer = document.getElementById("malfunctions-list-container");
        listContainer && empty(listContainer);
        generateChecklist({
            el: listContainer,
            items: this.displayLogCollection,
            labelTemplate: malfunctionLogTemplate,
            isCheckable: false,
            selectCallback: () => false,
            context: this,
            translator: this.translator
        });
    }

    private createHeaderLog(group: MalfunctionDutyStatusLog[]) {
        let headerLog: MalfunctionDutyStatusLog = jsonClone(_.first(group));
        let malfunctionType = headerLog.malfunction;
        let helpButton = malfunctionType === "Malfunction" || malfunctionType === "Diagnostic";

        headerLog.id = `${ headerLog.id }${ uuid() }`;
        headerLog._header = true;
        headerLog._clearButton = !headerLog.malfunction?.match(/Diagnostic/gi) && !headerLog._clearedTime;
        headerLog._helpButton = helpButton;
        headerLog._status = headerLog.status.replace("Compliance", "").split(constants.CAMELCASE_SEPARATOR_REGEXP).join(constants.SPACE_SEPARATOR);
        headerLog._malfunctionType = malfunctionType;
        headerLog.className = "malfunction-header";

        group.unshift(headerLog);
    }

    private populateNonHeaderLogs(collection: MalfunctionDutyStatusLog[]) {
        collection.forEach(log => {
            if (!log._header) {
                log._date = log._date || dayjs(dstCalculator.toLocalDateTime(log.dateTime)).format(DATE_FORMAT);
                log._header = false;
                log._status = log.status;
                log._malfunctionType = log.malfunction;
                log._clearedTime = log._clearedTime ? dayjs(dstCalculator.toLocalDateTime(log._clearedTime)).format(DATE_FORMAT) : log._clearedTime;
            }
        });
    }

    private updateLogs() {
        this.displayLogCollection = this.populateAndGroupLogs(this.getFilteredLogs());
        this.render();
    }

    private getFilteredLogs() {
        return this.loadLogs().filter(this.filterMalfunctions);
    }

    private addClearingLog(e) {
        e.target.setAttribute("disabled", "true");

        let clickedLog;
        for (let i = 0; i < this.displayLogCollection.length; i++) {
            let log = this.displayLogCollection[i];

            if (e.target.getAttribute("data-id") === log.id) {
                clickedLog = log;
                continue;
            }
        }

        if (clickedLog) {
            let newLog = {} as any;
            newLog.id = idSerializer.get();
            newLog.malfunction = `User${ clickedLog.malfunction }Clear`;
            newLog.status = clickedLog.status;
            newLog.dateTime = getServerDate().toISOString();
            newLog.origin = MANUAL_PROP;
            newLog.device = clickedLog.device;
            newLog.driver = { id: "NoUserId" };

            this.saveLog(newLog);
        }
    }

    private displayHelpDialog(e) {
        let helpDialogTemplate = e.target.getAttribute("data-malfunction") === "Malfunction" ? this.malfunctionHelpTemplate : this.diagnosticHelpTemplate;
        let helpDialogBox = new Dialog(
            "help-dialog",
            helpDialogTemplate({ statusId: e.target.getAttribute("data-status").replace(/ /g, "") }),
            this.translator,
            "Help",
            [{
                text: "OK",
                id: "help-dialog-ok",
                callback: () => {
                    helpDialogBox.remove();
                }
            }],
            {
                showCloseIcon: true
            }
        );

        void helpDialogBox.open();
    }

    private saveLog(log: any) {
        this.sync.dutyStatusLogs.addItem(log, false).then(this.updateLogs.bind(this)).catch(this.handleErrors);
    }

    private readonly handleErrors = (errors = [] as any[]) => {
        let errorsArray = Array.isArray(errors) ? errors : [errors];

        errorsArray.forEach(error => {
            if (error) {
                error instanceof PageError ? this.errors.push(error) : handleJSNative(error instanceof Error ? error : new Error(error));
            }
        });

        this.render();
    };
}